import { cn } from "@/lib/utils";
import type { DBFreelancerDiscipline } from "@/lib/db-types";

interface Props {
  disciplines: DBFreelancerDiscipline[];
  /** Valgte fagfelt i rekkefølge. Første er hovedfagfelt. */
  value: string[];
  onChange: (ids: string[]) => void;
}

/**
 * Fagfelt som chips. Klikk velger eller fjerner; dobbeltklikk (eller Enter
 * med Shift) gjør et valgt fagfelt til hovedfagfelt. Hovedfagfeltet står først
 * og er merket med ★.
 */
export function DisciplinePicker({ disciplines, value, onChange }: Props) {
  const active = disciplines.filter((d) => d.is_active || value.includes(d.id));

  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  const makePrimary = (id: string) => onChange([id, ...value.filter((v) => v !== id)]);

  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Fagfelt">
      {active.map((d) => {
        const index = value.indexOf(d.id);
        const selected = index >= 0;
        const primary = index === 0;
        return (
          <button
            key={d.id}
            type="button"
            aria-pressed={selected}
            title={selected && !primary ? "Dobbeltklikk for å gjøre til hovedfagfelt" : undefined}
            onClick={() => toggle(d.id)}
            onDoubleClick={() => selected && makePrimary(d.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.shiftKey && selected) {
                e.preventDefault();
                makePrimary(d.id);
              }
            }}
            className={cn(
              "inline-flex h-7 items-center gap-1 rounded-pill border-[1.5px] px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              selected ? "border-foreground bg-foreground text-background" : "border-border bg-card hover:border-foreground/40",
            )}
          >
            {primary && <span aria-label="hovedfagfelt">★</span>}
            {d.label}
          </button>
        );
      })}
    </div>
  );
}
